import { type RefObject, useCallback, useEffect, useRef, useState } from "react";
import { setMicLoudness } from "@acto/web-common/base-components";
import { useAuthContext } from "@acto/web-common/auth";
import { useLocale } from "@acto/web-common/locales";
import { useTranscribe } from "@acto/taskpane/core/features/transcription/useTranscribe";
import { QUIET_BEFORE_SETTLE_MS } from "./motion";
import { provisionalTail } from "./dictationTranscript";

const SILENCE_THRESHOLD = 0.02;
/** Speech rarely gets louder than this, so it is the top of the indicator's range rather than 1.0. */
const LOUDEST_EXPECTED = 0.25;
/**
 * The server finalizes on its own silence timeout too; this must stay below it, or the server's
 * `end` lands first and the stop is no longer this side's decision.
 */
const SILENCE_AFTER_SPEECH_MS = 4000;
/**
 * The trailing-pause rule cannot apply before the first word — taking a breath would end the
 * recording — but an open mic still needs a limit.
 */
const FIRST_WORD_TIMEOUT_MS = 15000;

export type DictationNotice = "none" | "nothing-heard";

/**
 * `stalled` is a session the engine ended on its own — server silence, socket error — while the
 * user still holds the field. Only a user-driven ending leaves it.
 */
type DictationStatus = "idle" | "opening" | "listening" | "stalled";

interface UseChatDictationOptions {
  /** Dictation splices onto whatever is already in the field. */
  value: string;
  /** Element the mic loudness is written to as a CSS variable, via `setMicLoudness`. */
  loudnessTarget?: RefObject<HTMLElement | null>;
  onChange: (value: string) => void;
}

export interface UseChatDictationResult {
  /** The engine has a live session: the socket is open and the server has acknowledged it. */
  recording: boolean;
  /**
   * The microphone could not be opened — refused, absent, or unsupported — so there is nothing to
   * dictate with. It holds for as long as this hook is mounted and cannot clear itself: permission
   * is granted outside the app, and the platform offers no way to observe that happening.
   */
  micBlocked: boolean;
  /**
   * Set when the user taps the mic, cleared only when they take the field back. The engine losing
   * the recording does not end it.
   */
  dictating: boolean;
  settling: boolean;
  provisionalLength: number;
  notice: DictationNotice;
  start: () => void;
  stop: () => void;
  /** Ends recording and keeps the transcript without entering the retry state. */
  interrupt: () => void;
}

interface DictationSession {
  textBeforeDictation: string;
  previousTranscript: string;
  settledWords: number;
  heardSomething: boolean;
  /**
   * A word has been transcribed — loudness alone must not set it. A sigh or a breath clears the
   * threshold, and counting that as the first word drops the session onto the trailing-pause budget
   * while the user is still thinking.
   */
  hasSpoken: boolean;
  lastSoundAt: number;
  live: boolean;
  /**
   * Outlives `live` by design: the engine's parting message is its normalizer's rewrite of what
   * was said, and that arrives after the stop request. Only the user taking the field back closes
   * it early.
   */
  acceptingTranscripts: boolean;
}

export function useChatDictation({ value, onChange, loudnessTarget }: UseChatDictationOptions): UseChatDictationResult {
  const { user } = useAuthContext();
  const { locale } = useLocale();

  const [status, setStatus] = useState<DictationStatus>("idle");
  const [settling, setSettling] = useState(false);
  const [provisionalLength, setProvisionalLength] = useState(0);
  /** Carries the composer text it was raised against, so text already in the field cannot clear it. */
  const [notice, setNotice] = useState<{ kind: DictationNotice; raisedAt: string } | null>(null);
  const [micBlocked, setMicBlocked] = useState(false);

  const sessionRef = useRef<DictationSession>({
    textBeforeDictation: "",
    previousTranscript: "",
    settledWords: 0,
    heardSomething: false,
    hasSpoken: false,
    lastSoundAt: 0,
    live: false,
    acceptingTranscripts: false,
  });

  // Read by the teardown, which must not depend on `value`: that would rebuild the silence timer on
  // every transcript.
  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = value;
  });

  const wasRecordingRef = useRef(false);
  // Monotonic across sessions on purpose: a resolving start compares the id it was given against
  // this to find out whether it has been superseded.
  const startRequestRef = useRef(0);

  const writeLoudness = useCallback(
    (normalised: number) => {
      setMicLoudness(loudnessTarget?.current, normalised);
    },
    [loudnessTarget],
  );

  const handleTranscript = useCallback(
    (sessionText: string) => {
      const session = sessionRef.current;

      if (!session.acceptingTranscripts || !sessionText) return;

      session.heardSomething = true;
      session.hasSpoken = true;
      // Speech under the loudness threshold still arrives as text, so the transcript has to carry
      // the clock for a soft speaker.
      session.lastSoundAt = performance.now();

      const separator = session.textBeforeDictation.trim() ? " " : "";
      onChange(session.textBeforeDictation + separator + sessionText);

      const tail = provisionalTail(session.previousTranscript, sessionText, session.settledWords);

      // A transcript that lands after the session closed has nothing left to revise.
      if (session.live) setProvisionalLength(tail.length);
      session.previousTranscript = sessionText;
      session.settledWords = tail.settledWords;
    },
    [onChange],
  );

  const handleLoudness = useCallback(
    (loudness: number) => {
      writeLoudness(Math.min(loudness / LOUDEST_EXPECTED, 1));

      if (loudness < SILENCE_THRESHOLD) return;

      sessionRef.current.lastSoundAt = performance.now();

      setSettling(false);
    },
    [writeLoudness],
  );

  const { isRecording, startRecording, stopRecording } = useTranscribe({
    onTranscript: handleTranscript,
    onLoudness: handleLoudness,
    tenant: user?.tenantId,
    user: user?.email,
    language: locale,
  });

  const openSession = useCallback((textBeforeDictation: string) => {
    sessionRef.current = {
      textBeforeDictation,
      previousTranscript: "",
      settledWords: 0,
      heardSomething: false,
      hasSpoken: false,
      lastSoundAt: performance.now(),
      live: true,
      acceptingTranscripts: true,
    };

    setProvisionalLength(0);
  }, []);

  /** Settles a session exactly once, for whichever ending reaches it first. */
  const closeSession = useCallback(
    (promptRetry: boolean) => {
      const session = sessionRef.current;

      if (!session.live) return;
      session.live = false;

      startRequestRef.current += 1;

      writeLoudness(0);
      setProvisionalLength(0);
      // Otherwise a session that ended quiet starts the next one already settling.
      setSettling(false);

      if (promptRetry && !session.heardSomething) {
        setNotice({ kind: "nothing-heard", raisedAt: valueRef.current });
      }
    },
    [writeLoudness],
  );

  const stop = useCallback(() => {
    const session = sessionRef.current;

    // A dead session's engine has already torn itself down; a second teardown races the first.
    if (session.live) {
      void stopRecording().finally(() => {
        session.acceptingTranscripts = false;
      });
    } else {
      session.acceptingTranscripts = false;
    }

    closeSession(true);
    setStatus("idle");
  }, [closeSession, stopRecording]);

  const interrupt = useCallback(() => {
    const session = sessionRef.current;

    session.acceptingTranscripts = false;

    if (session.live) void stopRecording();

    closeSession(false);
    setStatus("idle");
  }, [closeSession, stopRecording]);

  const start = useCallback(() => {
    const requestId = ++startRequestRef.current;

    setNotice(null);
    openSession(value);
    setStatus("opening");

    void startRecording().then((started) => {
      if (requestId !== startRequestRef.current) return;

      if (!started) {
        // A session that never opened cannot also have heard nothing, so it reports micBlocked
        // without raising the notice.
        closeSession(false);
        setStatus("idle");
        setMicBlocked(true);

        return;
      }

      // The permission prompt sits between the tap and here, and must not be charged against the
      // silence rules.
      sessionRef.current.lastSoundAt = performance.now();
      setStatus("listening");
    });
  }, [closeSession, openSession, startRecording, value]);

  const checkSilence = useCallback(() => {
    const session = sessionRef.current;

    if (!session.live) return;

    const quietFor = performance.now() - session.lastSoundAt;

    if (quietFor >= QUIET_BEFORE_SETTLE_MS) {
      setSettling(true);
      writeLoudness(0);
    }

    const timeout = session.hasSpoken ? SILENCE_AFTER_SPEECH_MS : FIRST_WORD_TIMEOUT_MS;

    if (quietFor >= timeout) {
      stop();
    }
  }, [stop, writeLoudness]);

  // A session can also end with nobody asking: the engine drops it on server silence or an error.
  useEffect(() => {
    const wasRecording = wasRecordingRef.current;
    wasRecordingRef.current = isRecording;

    if (!wasRecording || isRecording) return;
    // A session the user already ended reaches this effect too, and must not be re-flagged stalled.
    if (!sessionRef.current.live) return;

    closeSession(true);
    setStatus("stalled");
  }, [isRecording, closeSession]);

  useEffect(() => {
    if (status !== "listening") return;

    const timer = window.setInterval(checkSilence, 250);

    return () => window.clearInterval(timer);
  }, [checkSilence, status]);

  useEffect(() => {
    setNotice((current) => (current && current.raisedAt !== value ? null : current));
  }, [value]);

  useEffect(() => {
    if (status === "idle") return;

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        stop();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [status, stop]);

  return {
    recording: isRecording,
    micBlocked,
    dictating: status !== "idle",
    settling,
    provisionalLength,
    notice: notice?.kind ?? "none",
    start,
    stop,
    interrupt,
  };
}
